/**
 * What one live scan run reports to the server (scan_perf, one row per run).
 *
 * WHY "SUCCESS" NEEDED A SECOND INPUT (2026-09-15)
 *   The run reporter counted a run as a success only when the Pokédex GRID
 *   committed an entry (dexScanTiming's `committed`). A session spent on
 *   individual Pokémon pages saves My Pokémon records instead, and finish()
 *   then ticks their dex cells through the bulk cross-write, which the grid
 *   counter never sees. Every such run was reported as `success: false`, and
 *   the admin table read 18 of its 27 "tried, never committed" accounts off
 *   trainers whose scans had worked: 185 Pokémon saved and 189 dex cells
 *   ticked, reported as a failure.
 *
 *   So a run is a success when EITHER path wrote something, and `source`
 *   says which screen the run was really on, so the OCR panel can split the
 *   two workloads instead of averaging a 40-minute storage crawl into the
 *   grid's per-frame medians.
 */
import type { Platform } from "./platform";
import { classifyLiveScanOutcome, type LiveScanSignals, type ScanOutcome } from "./scanOutcomes";

export type ScanRunSource = "pokedex" | "pokemon";

export interface ScanRunReport {
  /** 'pokedex' for the grid, 'pokemon' for a run that only saved Pokémon. */
  source: ScanRunSource;
  /** Which OCR engine the platform implies; the admin panel groups by it. */
  engine: "apple-vision" | "ml-kit" | "web";
  /** The run wrote at least one dex entry OR saved at least one Pokémon. */
  success: boolean;
  /**
   * Why not, when it did not (2026-10-15, lib/scanOutcomes): null on a
   * success; "unknown" on a failure the caller gave no signals for.
   */
  outcome: ScanOutcome | null;
}

export function describeScanRun(input: {
  /** Distinct dex entries the grid committed this run. */
  committed: number;
  /** My Pokémon records this run saved (individual-page reads). */
  myPokemonSaves: number;
  platform: Platform;
  /** What the hook saw during the run; decides the outcome of a failure. */
  signals?: LiveScanSignals;
}): ScanRunReport {
  const engine =
    input.platform === "ios" ? "apple-vision" : input.platform === "android" ? "ml-kit" : "web";
  // A mixed run (grid, then a few detail pages) stays 'pokedex': the grid is
  // the workload that shaped its frame timings. Only a run with no grid
  // commits and at least one save is a Pokémon run.
  const source: ScanRunSource =
    input.committed === 0 && input.myPokemonSaves > 0 ? "pokemon" : "pokedex";
  const success = input.committed > 0 || input.myPokemonSaves > 0;
  const outcome = success ? null : input.signals ? classifyLiveScanOutcome(input.signals) : "unknown";
  return { source, engine, success, outcome };
}
